import type { ReactNode } from "react";
import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Zap } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "../utils/cn";

interface HowToStep {
  readonly id: string;
  readonly title: string;
  readonly body: ReactNode;
}

const DEFAULT_STEPS: readonly HowToStep[] = [
  {
    id: "invite",
    title: "1. Invite Friends, Earn Points",
    body: "Share your referral link and earn points for each person who joins. The more you bring in, the higher you climb on the leaderboard.",
  },
  {
    id: "trade",
    title: "2. Trade to Boost Your Rank",
    body: (
      <>
        Earn points from your own trades and from the trading volume of your
        referrals. More volume means more points, and more{" "}
        <span className="font-medium text-accent-light">$TRIGGER</span> in the
        end.
      </>
    ),
  },
  {
    id: "airdrop",
    title: "3. Get Airdropped and Win Prizes",
    body: (
      <>
        At the end of the season, your points convert into{" "}
        <span className="font-medium text-accent-light">$TRIGGER</span>{" "}
        airdrops.
      </>
    ),
  },
];

const TRACK_W = 28;
const LINE_X = TRACK_W / 2;
const DOT_R = 7;

function sanitizeSvgId(raw: string): string {
  return `howto-${raw.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

interface TimelineGeom {
  readonly centers: readonly number[];
  readonly trackHeight: number;
  /** Y (track coords) where the connector line ends — past the last dot, to the last card bottom. */
  readonly lineEndY: number;
}

function isElementInViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  const edgeInset = 50;

  return (
    rect.top < window.innerHeight - edgeInset &&
    rect.bottom > edgeInset &&
    rect.left < window.innerWidth &&
    rect.right > 0
  );
}

function collectScrollTargets(element: HTMLElement | null): HTMLElement[] {
  const targets: HTMLElement[] = [];
  let node = element?.parentElement ?? null;

  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") {
      targets.push(node);
    }
    node = node.parentElement;
  }

  return targets;
}

function geomEqual(a: TimelineGeom, b: TimelineGeom): boolean {
  if (
    a.trackHeight !== b.trackHeight ||
    Math.abs(a.lineEndY - b.lineEndY) > 0.5 ||
    a.centers.length !== b.centers.length
  ) {
    return false;
  }
  return a.centers.every((y, i) => Math.abs(y - (b.centers[i] ?? 0)) < 0.5);
}

interface HowToStepsProps {
  readonly title?: string;
  readonly steps?: readonly HowToStep[];
  readonly className?: string;
  readonly hideTitle?: boolean;
}

export function HowItWorks({
  title = "Refer Friends To Get Early Access To Trigger",
  steps = DEFAULT_STEPS,
  className,
  hideTitle = false,
}: HowToStepsProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [geom, setGeom] = useState<TimelineGeom>({
    centers: [],
    trackHeight: 0,
    lineEndY: 0,
  });
  const uid = useId();
  const lineGradId = sanitizeSvgId(`${uid}-line-grad`);
  const revealedStepIdsRef = useRef<Set<string>>(new Set());
  const [visibleStepIds, setVisibleStepIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const measure = useCallback(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    rowRefs.current.length = steps.length;
    const nextCenters: number[] = [];

    for (let i = 0; i < steps.length; i++) {
      const li = rowRefs.current[i];
      if (!li) {
        continue;
      }
      nextCenters.push(li.offsetTop + li.offsetHeight / 2);
    }

    const lastIndex = steps.length - 1;
    const lastLi = lastIndex >= 0 ? rowRefs.current[lastIndex] : null;
    const lastCenter = nextCenters.at(-1);
    let lineEndY = lastCenter ?? 0;
    if (lastLi) {
      lineEndY = Math.min(lastLi.offsetTop + lastLi.offsetHeight, track.offsetHeight);
    }
    if (lastCenter != null && lineEndY < lastCenter) {
      lineEndY = lastCenter;
    }

    const next: TimelineGeom = {
      centers: nextCenters,
      trackHeight: track.offsetHeight,
      lineEndY,
    };
    setGeom((prev) => (geomEqual(prev, next) ? prev : next));
  }, [steps]);

  useLayoutEffect(() => {
    measure();
    const id = requestAnimationFrame(() => {
      measure();
    });

    const track = trackRef.current;
    if (!track) {
      return () => cancelAnimationFrame(id);
    }

    const ro = new ResizeObserver(() => {
      measure();
    });
    ro.observe(track);

    window.addEventListener("resize", measure);

    return () => {
      cancelAnimationFrame(id);
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  useLayoutEffect(() => {
    let cancelled = false;
    const observers: IntersectionObserver[] = [];

    revealedStepIdsRef.current = new Set();
    setVisibleStepIds(new Set());

    const revealStep = (stepId: string) => {
      if (revealedStepIdsRef.current.has(stepId)) {
        return;
      }

      revealedStepIdsRef.current.add(stepId);
      setVisibleStepIds((prev) => {
        if (prev.has(stepId)) {
          return prev;
        }

        const next = new Set(prev);
        next.add(stepId);
        return next;
      });
    };

    const revealVisibleSteps = () => {
      if (cancelled) {
        return;
      }

      for (let index = 0; index < steps.length; index++) {
        const step = steps[index];
        const element = rowRefs.current[index];
        if (!step || !element || revealedStepIdsRef.current.has(step.id)) {
          continue;
        }

        if (isElementInViewport(element)) {
          revealStep(step.id);
        }
      }
    };

    const setupObservers = () => {
      if (cancelled || steps.length === 0) {
        return;
      }

      for (let index = 0; index < steps.length; index++) {
        const step = steps[index];
        const element = rowRefs.current[index];
        if (!step || !element || revealedStepIdsRef.current.has(step.id)) {
          continue;
        }

        const observer = new IntersectionObserver(
          ([entry]) => {
            if (!entry?.isIntersecting) {
              return;
            }

            revealStep(step.id);
            observer.unobserve(element);
          },
          {
            threshold: 0.1,
            rootMargin: "0px 0px -50px 0px",
          }
        );

        observer.observe(element);
        observers.push(observer);
      }
    };

    const onScroll = () => {
      revealVisibleSteps();
    };

    let scrollTargets: HTMLElement[] = [];
    let revealFrame = 0;

    const frame = requestAnimationFrame(() => {
      setupObservers();

      revealFrame = requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }

        revealVisibleSteps();

        window.addEventListener("scroll", onScroll, { passive: true });
        scrollTargets = collectScrollTargets(trackRef.current);
        for (const target of scrollTargets) {
          target.addEventListener("scroll", onScroll, { passive: true });
        }
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      cancelAnimationFrame(revealFrame);
      window.removeEventListener("scroll", onScroll);
      for (const target of scrollTargets) {
        target.removeEventListener("scroll", onScroll);
      }
      for (const observer of observers) {
        observer.disconnect();
      }
    };
  }, [steps]);

  const { centers, trackHeight, lineEndY } = geom;
  const y0 = centers[0];
  const showLine =
    centers.length >= 2 &&
    y0 !== undefined &&
    Number.isFinite(y0) &&
    Number.isFinite(lineEndY) &&
    lineEndY > y0;

  return (
    <section className={cn("min-w-0 flex flex-col gap-6", className)}>
      {!hideTitle ? (
        <h2 className="text-lg font-semibold tracking-tight text-white">
          {title}
        </h2>
      ) : null}

      <div ref={trackRef} className="relative min-h-0">
        <ul className="relative z-0 m-0 list-none space-y-6 p-0 pl-8">
          {steps.map((step, index) => (
            <li
              key={step.id}
              ref={(el) => {
                rowRefs.current[index] = el;
              }}
              className="min-w-0"
            >
              <div
                className={cn(
                  "transition-[opacity,transform] duration-700 ease-out will-change-[opacity,transform]",
                  visibleStepIds.has(step.id)
                    ? "translate-y-0 opacity-100"
                    : "translate-y-8 opacity-0"
                )}
              >
                <Card className="min-w-0 border border-border bg-tertiary py-0 ring-0 shadow-none">
                  <CardContent className="flex flex-col gap-1 p-3">
                    <div className="flex gap-3">
                      <Zap
                        className="mt-0.5 size-5 shrink-0 text-foreground"
                        strokeWidth={2}
                        aria-hidden
                      />
                      <h4 className="text-base font-semibold text-white">
                        {step.title}
                      </h4>
                    </div>
                    <p className="m-0 text-sm leading-relaxed text-text-secondary">
                      {step.body}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </li>
          ))}
        </ul>

        <div
          className="pointer-events-none absolute left-0 top-0 z-[1] w-7 select-none overflow-visible"
          style={{ height: Math.max(trackHeight, 1) }}
          aria-hidden
        >
          {trackHeight > 0 && centers.length > 0 ? (
            <svg
              width={TRACK_W}
              height={trackHeight}
              className="block overflow-visible"
              shapeRendering="geometricPrecision"
            >
              <defs>
                <linearGradient
                  id={lineGradId}
                  gradientUnits="userSpaceOnUse"
                  x1={LINE_X}
                  x2={LINE_X}
                  y1={showLine ? y0 : 0}
                  y2={showLine ? lineEndY : trackHeight}
                >
                  <stop offset="0%" stopColor="#03ffa7" stopOpacity="1" />
                  <stop offset="58%" stopColor="#03ffa7" stopOpacity="0.65" />
                  <stop offset="82%" stopColor="#03ffa7" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="#03ffa7" stopOpacity="0" />
                </linearGradient>
              </defs>

              {showLine ? (
                <line
                  x1={LINE_X}
                  y1={y0}
                  x2={LINE_X}
                  y2={lineEndY}
                  stroke={`url(#${lineGradId})`}
                  strokeWidth={2}
                  strokeLinecap="round"
                  style={{
                    filter: "drop-shadow(0 0 4px rgba(3, 255, 167, 0.55))",
                  }}
                />
              ) : null}

              {centers.map((cy, i) => {
                const stepId = steps[i]?.id;
                const isVisible = stepId ? visibleStepIds.has(stepId) : false;

                return (
                  <circle
                    key={stepId ?? i}
                    cx={LINE_X}
                    cy={cy}
                    r={DOT_R}
                    fill="var(--background)"
                    stroke="#03ffa7"
                    strokeWidth={2}
                    className={cn(
                      "transition-opacity duration-700 ease-out",
                      isVisible ? "opacity-100" : "opacity-0"
                    )}
                    style={{
                      filter: "drop-shadow(0 0 6px rgba(3, 255, 167, 0.45))",
                    }}
                  />
                );
              })}
            </svg>
          ) : null}
        </div>
      </div>
    </section>
  );
}
